import { MeasureType } from 'domains/measurementDomain';
import { useMeasurementUnit } from 'hooks/useMeasurementUnit';

type Props = {
  /** The imperial value, exactly as the API returned it. */
  value?: number | string | null;
  /**
   * The metric companion the API sent alongside it.
   *
   * Required, not optional: the API sends both units precisely so switching is instant, and a
   * caller that forgets this renders the imperial number under a metric header - which is how
   * every one of these tables kept showing inches after a switch.
   */
  metric: number | string | null | undefined;
  /** Which quantity this is, so the right unit and conversion are used. */
  type?: MeasureType;
  /** Set false to render the number alone, e.g. inside a `10 x 8` pair. */
  withSymbol?: boolean;
  /** Shown when there is no value. */
  placeholder?: string;
  className?: string;
};

/**
 * Renders a numeric measurement, picking between the two units the API sent.
 *
 * Nothing is converted here. Every numeric measurement is converted by the API - either from a
 * stored metric twin or at its JSON boundary - so the number that arrives is the one to show, and
 * scaling it again would render an 8.5in sheet as 5483.86mm.
 *
 * Size strings and basis weights are the exception and still convert client-side, because those
 * double as the values the UI matches selections on; see `displaySize` and `basisWeight`.
 */
export const RevxMeasure = ({
  value,
  metric,
  type = MeasureType.LENGTH,
  withSymbol = true,
  placeholder = '-',
  className,
}: Props) => {
  const { format } = useMeasurementUnit();
  const text = format(value, type, { withSymbol, metric });
  return <span className={className}>{text || placeholder}</span>;
};
